import { db, storage } from '../firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  documentId,
  serverTimestamp,
  increment,
  setDoc,
  onSnapshot
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { composeFeatured } from './featured';
import { getFollowing, getUsersByIds } from './userService';
import { isAllergyTag, matchesAllergyFilters } from './conversions';

const RECIPES_COLLECTION = 'recipes';
const RATINGS_COLLECTION = 'ratings';
const COMMENTS_COLLECTION = 'comments';
const SAVES_COLLECTION = 'saves';

// Upload recipe image
export async function uploadRecipeImage(file, userId) {
  const fileRef = ref(storage, `recipes/${userId}/${Date.now()}_${file.name}`);
  await uploadBytes(fileRef, file);
  return getDownloadURL(fileRef);
}

// Create a new recipe
export async function createRecipe(recipeData, userId) {
  const recipe = {
    ...recipeData,
    authorId: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    averageRating: 0,
    totalRatings: 0,
    viewCount: 0,
  };

  const docRef = await addDoc(collection(db, RECIPES_COLLECTION), recipe);
  return docRef.id;
}

// Get a single recipe
export async function getRecipe(recipeId) {
  const docRef = doc(db, RECIPES_COLLECTION, recipeId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
}

// Fetch many recipes by ID in one read per 10 (Firestore's `in` limit),
// instead of one getRecipe() per ID. Order is not preserved and missing
// (e.g. deleted) IDs simply don't come back -- callers that need ordering
// or the ID->something mapping should re-key the result themselves.
export async function getRecipesByIds(ids) {
  const unique = [...new Set(ids)].filter(Boolean);
  if (unique.length === 0) return [];
  const chunks = [];
  for (let i = 0; i < unique.length; i += 10) {
    chunks.push(unique.slice(i, i + 10));
  }
  // A chunk query is all-or-nothing under security rules: for a signed-out
  // caller, ONE non-public/flagged doc in the chunk denies the whole query
  // (rules evaluate per result doc). Fall back to individual reads so only
  // the genuinely unreadable docs drop out instead of the entire batch --
  // e.g. one private linked recipe must not hide a public recipe's other
  // links from anonymous visitors.
  const chunkResults = await Promise.all(chunks.map(chunk =>
    getDocs(query(
      collection(db, RECIPES_COLLECTION),
      where(documentId(), 'in', chunk)
    ))
      .then(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })))
      .catch(() => Promise.all(chunk.map(id =>
        getDoc(doc(db, RECIPES_COLLECTION, id))
          .then(s => (s.exists() ? { id: s.id, ...s.data() } : null))
          .catch(() => null)
      )).then(docs => docs.filter(Boolean)))
  ));
  return chunkResults.flat();
}

// Subscribe to a recipe's live updates (ratings, server-computed nutrition).
// Returns the unsubscribe function.
export function subscribeToRecipe(recipeId, callback) {
  return onSnapshot(
    doc(db, RECIPES_COLLECTION, recipeId),
    (snap) => callback(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    // A denied read (private recipe, anonymous or otherwise unauthorized
    // viewer) never calls the success handler above -- without this, the
    // caller's loading state would spin forever instead of resolving to
    // "not found". Now that recipes can be conditionally public, a denial
    // is a real, reachable case, not just a theoretical one.
    () => callback(null)
  );
}

// Get all recipes with optional filters
// `publicOnly` is required for signed-out callers. The recipes read rule is
// a plain `publiclyVisible == true` comparison, so Firestore denies any
// anonymous list query that doesn't constrain on it -- that constraint is
// what guarantees a signed-out visitor can only ever receive recipes that
// are genuinely public, rather than relying on client-side filtering.
export async function getRecipes({ dietaryFilters = [], cuisineFilter = '', mealType = '', sortBy = 'createdAt', pageSize = 20, lastDoc = null, publicOnly = false } = {}) {
  let q = collection(db, RECIPES_COLLECTION);
  const constraints = [];

  if (publicOnly) {
    constraints.push(where('publiclyVisible', '==', true));
  }

  if (dietaryFilters.length > 0) {
    // Firestore array-contains can only filter one value at a time
    // For MVP, filter by first dietary tag, then filter rest client-side
    constraints.push(where('dietaryTags', 'array-contains', dietaryFilters[0]));
  }

  if (cuisineFilter) {
    constraints.push(where('cuisineTags', 'array-contains', cuisineFilter));
  }

  if (mealType) {
    constraints.push(where('mealType', '==', mealType));
  }

  constraints.push(orderBy(sortBy === 'rating' ? 'averageRating' : 'createdAt', 'desc'));
  constraints.push(limit(pageSize));

  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  q = query(q, ...constraints);
  const snapshot = await getDocs(q);

  let recipes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Client-side filter for additional dietary tags
  if (dietaryFilters.length > 1) {
    recipes = recipes.filter(recipe =>
      dietaryFilters.every(tag => recipe.dietaryTags?.includes(tag))
    );
  }

  return {
    recipes,
    lastDoc: snapshot.docs[snapshot.docs.length - 1] || null
  };
}

// Curated "new & top rated" ranking lives in ranking.js (pure, unit-tested);
// re-exported here so pages keep importing everything from the service.
export { rankCurated } from './ranking';

// Get recipes by a specific user
export async function getUserRecipes(userId) {
  const q = query(
    collection(db, RECIPES_COLLECTION),
    where('authorId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// An author's recipes for a SIGNED-OUT viewer. Must constrain on
// publiclyVisible: the read rule is a plain comparison on that field, so an
// anonymous list query that doesn't include it is denied outright. It also
// replaces the older moderationStatus == 'approved' constraint, which no
// longer matches the rule and would now be refused.
// No orderBy: equality-only filters need no composite index; sort here.
export async function getPublicUserRecipes(userId) {
  const q = query(
    collection(db, RECIPES_COLLECTION),
    where('authorId', '==', userId),
    where('publiclyVisible', '==', true)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() }))
    .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
}

// Update a recipe
export async function updateRecipe(recipeId, data) {
  const docRef = doc(db, RECIPES_COLLECTION, recipeId);
  await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });
}

// Delete a recipe
export async function deleteRecipe(recipeId) {
  await deleteDoc(doc(db, RECIPES_COLLECTION, recipeId));
}

// Rate a recipe. The recipe's averageRating/totalRatings are recomputed
// server-side by the onRatingWritten Cloud Function.
export async function rateRecipe(recipeId, userId, rating) {
  const ratingRef = doc(db, RATINGS_COLLECTION, `${userId}_${recipeId}`);
  const existingRating = await getDoc(ratingRef);

  await setDoc(ratingRef, {
    userId,
    recipeId,
    rating,
    createdAt: existingRating.exists() ? existingRating.data().createdAt : serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}

// Get all recipes a user has rated, joined with their rating,
// sorted by that rating (highest first), then most recently rated.
export async function getRatedRecipes(userId) {
  const q = query(
    collection(db, RATINGS_COLLECTION),
    where('userId', '==', userId)
  );
  const snapshot = await getDocs(q);
  const ratingByRecipeId = new Map(
    snapshot.docs.map(d => [d.data().recipeId, d.data()])
  );

  // Batch-fetch the rated recipes (one read per 10 instead of one per
  // rating); deleted recipes simply don't come back. Re-key by ID to join
  // each recipe with the user's rating.
  const recipes = (await getRecipesByIds([...ratingByRecipeId.keys()]))
    .map(recipe => {
      const r = ratingByRecipeId.get(recipe.id);
      return { ...recipe, userRating: r.rating, ratedAt: r.updatedAt };
    });

  return recipes
    .sort((a, b) =>
      b.userRating - a.userRating ||
      (b.ratedAt?.toMillis?.() || 0) - (a.ratedAt?.toMillis?.() || 0)
    );
}

// Get user's rating for a recipe
export async function getUserRating(recipeId, userId) {
  const ratingRef = doc(db, RATINGS_COLLECTION, `${userId}_${recipeId}`);
  const ratingSnap = await getDoc(ratingRef);
  if (ratingSnap.exists()) {
    return ratingSnap.data().rating;
  }
  return null;
}

// ---- Saves (bookmarks) ---------------------------------------------------
// Doc ID is "{userId}_{recipeId}", the same shape ratings and follows use.
// That makes save/unsave idempotent: saving twice writes the same doc rather
// than accumulating duplicates, so no read is needed before a write.

const saveId = (userId, recipeId) => `${userId}_${recipeId}`;

export async function saveRecipe(recipeId, userId) {
  await setDoc(doc(db, SAVES_COLLECTION, saveId(userId, recipeId)), {
    userId,
    recipeId,
    createdAt: serverTimestamp()
  });
}

export async function unsaveRecipe(recipeId, userId) {
  await deleteDoc(doc(db, SAVES_COLLECTION, saveId(userId, recipeId)));
}

export async function isSaved(recipeId, userId) {
  const snap = await getDoc(doc(db, SAVES_COLLECTION, saveId(userId, recipeId)));
  return snap.exists();
}

// Just the ids, for the app-wide saved set that drives every bookmark
// button. One query for the whole session instead of an isSaved() read per
// card, which on a 20-card grid was 20 reads to draw 20 icons.
export async function getSavedRecipeIds(userId) {
  const snapshot = await getDocs(query(
    collection(db, SAVES_COLLECTION),
    where('userId', '==', userId)
  ));
  return snapshot.docs.map(d => d.data().recipeId);
}

// Mirrors getRatedRecipes: a single-field where() with no orderBy, so this
// needs no composite index. Sorting happens in memory afterwards -- a
// personal bookmark list is small enough that paging it would cost more
// than it saves.
export async function getSavedRecipes(userId) {
  const q = query(
    collection(db, SAVES_COLLECTION),
    where('userId', '==', userId)
  );
  const snapshot = await getDocs(q);
  const savedAtByRecipeId = new Map(
    snapshot.docs.map(d => [d.data().recipeId, d.data().createdAt])
  );

  // Deleted recipes simply don't come back from the batch fetch, so a save
  // pointing at a removed recipe drops out silently rather than rendering
  // as a broken card.
  const recipes = (await getRecipesByIds([...savedAtByRecipeId.keys()]))
    .map(recipe => ({ ...recipe, savedAt: savedAtByRecipeId.get(recipe.id) }));

  return recipes.sort(
    (a, b) => (b.savedAt?.toMillis?.() || 0) - (a.savedAt?.toMillis?.() || 0)
  );
}

// Report a recipe (spam, unsafe instructions, stolen content, etc.).
// Reports are write-only for clients; review happens in the Firebase console.
// Doc ID is "{reporterId}_{recipeId}", so one person can only ever hold one
// report per recipe. That's load-bearing now that two reports auto-hide a
// recipe: with auto-generated ids, one user could report the same recipe
// twice and take it down alone.
export async function reportRecipe(recipeId, userId, reason) {
  await setDoc(doc(db, 'reports', `${userId}_${recipeId}`), {
    recipeId,
    reporterId: userId,
    reason: (reason || '').slice(0, 1000),
    createdAt: serverTimestamp()
  });
}

// Comments on a recipe, newest first. Sorted client-side rather than via
// orderBy in the query -- avoids needing a composite index for the
// recipeId-equality + createdAt-order combination (same reasoning as
// getRecipesFromAuthors below). Read is open to anyone, signed in or not,
// so a public recipe's comments show as social proof to anonymous visitors.
export async function getComments(recipeId) {
  const snap = await getDocs(query(
    collection(db, COMMENTS_COLLECTION),
    where('recipeId', '==', recipeId)
  ));
  const millis = (c) => {
    const t = c.createdAt;
    if (t?.toMillis) return t.toMillis();
    if (typeof t?.seconds === 'number') return t.seconds * 1000;
    return 0;
  };
  const comments = snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => millis(b) - millis(a));

  // Join the authors' live profiles for display. Any name/photo still stored
  // on an OLD comment is ignored -- it was client-supplied and therefore
  // unverifiable. An author whose profile can't be read (private account, or
  // deleted) shows as "Chef", which is the correct outcome: a private account
  // shouldn't have its name republished through its comments.
  const profiles = await getUsersByIds(comments.map(c => c.userId)).catch(() => []);
  const byUid = new Map(profiles.map(p => [p.uid, p]));
  return comments.map(c => {
    const author = byUid.get(c.userId);
    return {
      ...c,
      userName: author?.displayName || 'Chef',
      userPhotoURL: author?.photoURL || null,
      userPhotoPosition: author?.photoPosition || 'center'
    };
  });
}

// Identity is deliberately not stored on the comment: userName/userPhotoURL
// used to be client-supplied, which let anyone post under someone else's
// name and photo. getComments joins the author's live profile instead.
export async function addComment(recipeId, userId, text) {
  const ref = await addDoc(collection(db, COMMENTS_COLLECTION), {
    recipeId,
    userId,
    text: text.trim(),
    createdAt: serverTimestamp()
  });
  return ref.id;
}

export async function deleteComment(commentId) {
  await deleteDoc(doc(db, COMMENTS_COLLECTION, commentId));
}

// Increment view count -- at most once per recipe per browser session, so
// revisits (or a scripted reload loop) don't inflate the counter or re-fire
// the onRecipeWritten trigger on every page load.
export async function incrementViewCount(recipeId) {
  const key = `viewed_${recipeId}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch {
    // sessionStorage unavailable (private mode, etc.) -- just count the view
  }
  const recipeRef = doc(db, RECIPES_COLLECTION, recipeId);
  await updateDoc(recipeRef, { viewCount: increment(1) });
}


// Recent recipes authored by any of the given uids (capped at Firestore's
// `in` limit). Sorted newest-first client-side to avoid needing a composite
// index for the in + orderBy combination.
async function getRecipesFromAuthors(authorUids) {
  const uids = authorUids.slice(0, 10);
  if (uids.length === 0) return [];
  const snap = await getDocs(query(
    collection(db, RECIPES_COLLECTION),
    where('authorId', 'in', uids),
    limit(30)
  ));
  const millis = (r) => {
    const c = r.createdAt;
    if (c?.toMillis) return c.toMillis();
    if (typeof c?.seconds === 'number') return c.seconds * 1000;
    return 0;
  };
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => millis(b) - millis(a));
}

// "Featured": 2 recipes from the recommender (similar to what you've
// reviewed/liked) + 1 recent recipe from someone you follow. When you follow
// no one yet, the follow slot backfills from the recommender so Featured
// still shows three. Composition logic lives in composeFeatured (pure/tested).
export async function getFeaturedRecipes(userId, userPreferences = []) {
  // This function and getRecommendedRecipes both need the caller's ratings,
  // and both used to fetch them -- the identical query, twice, in the same
  // Promise.all, on every Browse load for every signed-in user. Read once
  // and hand the rows down. Still one round trip: the ratings read runs in
  // parallel with getFollowing, exactly as before.
  const [myRatings, followedUids] = await Promise.all([
    fetchUserRatings(userId),
    getFollowing(userId)
  ]);
  const recs = await getRecommendedRecipes(userId, userPreferences, 6, myRatings);
  const ratedIds = new Set(myRatings.map(r => r.recipeId));

  let followedPick = null;
  if (followedUids.length > 0) {
    const allergyPrefs = userPreferences.filter(isAllergyTag);
    const fromFollows = await getRecipesFromAuthors(followedUids);
    followedPick = fromFollows.find(r =>
      r.moderationStatus !== 'flagged' && !r.communityFlagged &&
      r.authorId !== userId &&
      !ratedIds.has(r.id) &&
      matchesAllergyFilters(r, allergyPrefs)
    ) || null;
  }

  return composeFeatured(recs, followedPick, 3);
}

// Every rating this user has left. Shared by getFeaturedRecipes and
// getRecommendedRecipes so the Browse page reads them once, not twice.
async function fetchUserRatings(userId) {
  const snap = await getDocs(query(
    collection(db, RATINGS_COLLECTION),
    where('userId', '==', userId)
  ));
  return snap.docs.map(d => d.data());
}

// Recommendations: collaborative filtering ("raters who liked what you
// liked also liked…"), topped up with tag-based matches when there isn't
// enough rating overlap yet.
//
// `preloadedRatings` lets a caller that has already read this user's ratings
// pass them in rather than triggering a second identical query.
export async function getRecommendedRecipes(userId, userPreferences = [], limitCount = 10, preloadedRatings = null) {
  // Allergy prefs ("no peanuts") are hard EXCLUSIONS applied to every
  // candidate at the end, judged against the server's allergen scan --
  // they must never be positive match signals (recipes aren't tagged with
  // them anymore). Dietary prefs stay positive signals as before.
  const allergyPrefs = userPreferences.filter(isAllergyTag);
  const dietPrefs = userPreferences.filter(p => !isAllergyTag(p));
  const safe = recipes => recipes.filter(r => matchesAllergyFilters(r, allergyPrefs));

  // Everything the user has rated (any score) is excluded from results
  const myRatings = preloadedRatings || await fetchUserRatings(userId);
  const ratedIds = new Set(myRatings.map(r => r.recipeId));
  const likedIds = myRatings.filter(r => r.rating >= 4).map(r => r.recipeId);

  const collaborative = safe(await getCollaborativeCandidates(userId, likedIds, ratedIds, limitCount));
  if (collaborative.length >= limitCount) {
    return collaborative.slice(0, limitCount);
  }

  // Top up with tag-based candidates
  const tagBased = safe(await getTagBasedCandidates(likedIds, dietPrefs, limitCount));
  const seen = new Set(collaborative.map(r => r.id));
  const merged = [...collaborative];
  for (const recipe of tagBased) {
    if (merged.length >= limitCount) break;
    if (!seen.has(recipe.id) && !ratedIds.has(recipe.id)) {
      merged.push(recipe);
      seen.add(recipe.id);
    }
  }
  return merged;
}

// Peers = users who also rated >=4 on recipes this user rated >=4, weighted
// by how many liked recipes they share. Candidates = those peers' other
// highly rated recipes, scored by peer similarity x rating strength.
async function getCollaborativeCandidates(userId, likedIds, ratedIds, limitCount) {
  if (likedIds.length === 0) return [];

  const peerWeights = {};
  await Promise.all(likedIds.slice(0, 5).map(async (recipeId) => {
    const snap = await getDocs(query(
      collection(db, RATINGS_COLLECTION),
      where('recipeId', '==', recipeId),
      where('rating', '>=', 4),
      limit(15)
    ));
    snap.docs.forEach(d => {
      const raterId = d.data().userId;
      if (raterId !== userId) {
        peerWeights[raterId] = (peerWeights[raterId] || 0) + 1;
      }
    });
  }));

  const topPeers = Object.entries(peerWeights)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
  if (topPeers.length === 0) return [];

  const scores = {};
  await Promise.all(topPeers.map(async ([peerId, weight]) => {
    const snap = await getDocs(query(
      collection(db, RATINGS_COLLECTION),
      where('userId', '==', peerId),
      where('rating', '>=', 4),
      limit(30)
    ));
    snap.docs.forEach(d => {
      const { recipeId, rating } = d.data();
      if (ratedIds.has(recipeId)) return;
      scores[recipeId] = (scores[recipeId] || 0) + weight * (rating - 3);
    });
  }));

  const topIds = Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limitCount)
    .map(([id]) => id);
  // Batch-fetch, then restore the score order (getRecipesByIds doesn't
  // preserve it) and drop flagged recipes.
  const byId = new Map((await getRecipesByIds(topIds)).map(r => [r.id, r]));
  return topIds
    .map(id => byId.get(id))
    .filter(r => r && r.moderationStatus !== 'flagged' && !r.communityFlagged);
}

async function getTagBasedCandidates(likedIds, userPreferences, limitCount) {
  // Tags from liked recipes plus the user's stated dietary preferences
  const tagCounts = {};
  const likedRecipes = await getRecipesByIds(likedIds.slice(0, 10));
  for (const recipe of likedRecipes) {
    // Skip legacy "no X" allergy tags: new recipes never carry them, so as
    // a positive array-contains term they'd match nothing.
    [...(recipe.dietaryTags || []).filter(t => !isAllergyTag(t)), ...(recipe.cuisineTags || [])].forEach(tag => {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1;
    });
  }
  userPreferences.forEach(pref => {
    tagCounts[pref] = (tagCounts[pref] || 0) + 3; // weight preferences higher
  });

  const topTags = Object.entries(tagCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([tag]) => tag);

  if (topTags.length === 0) {
    // No signal at all: fall back to popular recipes
    const q = query(
      collection(db, RECIPES_COLLECTION),
      orderBy('averageRating', 'desc'),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(r => !likedIds.includes(r.id) && r.moderationStatus !== 'flagged' && !r.communityFlagged);
  }

  const q = query(
    collection(db, RECIPES_COLLECTION),
    where('dietaryTags', 'array-contains', topTags[0]),
    orderBy('averageRating', 'desc'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(r => !likedIds.includes(r.id) && r.moderationStatus !== 'flagged' && !r.communityFlagged);
}
